// The worm level and the daily challenges on the client (rules: core
// progression/progression.js; the server decides: edge-server
// progression/store.js). One small store the whole app reads: the start page's
// level and challenges, the cards in the game after every round.
//
// Signed in: the account's row (player_progress), then every round's
// progress_update from the room. A guest: each round arrives as a signed token
// (progress_guest), kept on this device; the level and challenges are worked
// out from them here, and once the guest signs in the tokens are handed in
// (claim-guest) and count for the account.
import { useEffect, useState } from "react";
import * as prog from "core/src/progression/progression.js";
import { supabase } from "./supabase";
import { dsHttpOrigin } from "../online/net";
import { refreshEggBalance } from "./shop/useEggBalance";

export interface Challenge { id: string; tier: number; metric: string; target: number; text: string; detail: string | null; have: number; done: boolean; xp: number }
export interface Progress {
  loaded: boolean;
  guest: boolean;      // the level is this device's, waiting for an account
  xp: number;
  level: number; into: number; need: number;
  day: string;
  challenges: Challenge[];
  guestRounds: number; // a guest's rounds kept on this device
  toClaim: number;     // Golden Eggs for days with all three done, waiting for a click (claimDaily)
}

// What the match so far brought (every round since resetMatchProgress), for
// the card in the match's end modal (MatchXp): the level it started at, where
// it is now, where the XP came from, the quests finished and the eggs.
export interface XpPart { id: string; n: number; xp: number }
export interface MatchProgress {
  xp: number;
  fromLevel: number; fromInto: number; fromNeed: number;
  level: number; into: number; need: number;
  parts: XpPart[];                     // the rounds' XP by kind: played, outlasted, won, gaps, crashes
  quests: { text: string; xp: number }[];
  levelEggs: number;                   // a level reached that pays eggs
  dayEggs: number;                     // (none since the quests' eggs are claimed on the start page)
}

const GUEST_KEY = "ar.guestProgress";
const MAX_TOKENS = 600; // what the server takes in one claim

// The player's own calendar day (the server counts days in the seat's time zone).
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function view(xp: number, raw: any, guest: boolean, guestRounds: number): Progress {
  const day = today();
  const lv = prog.levelOf(xp);
  return { loaded: true, guest, xp, level: lv.level, into: lv.into, need: lv.need, day, challenges: prog.challengeStatus(raw, day), guestRounds, toClaim: guest ? 0 : prog.claimableEggs(raw) };
}

let state: Progress = view(0, null, true, 0);
state.loaded = false;
let account: { userId: string; raw: any } | null = null;
const subs = new Set<(p: Progress) => void>();
const set = (p: Progress) => { state = p; subs.forEach((f) => f(state)); };
let match: MatchProgress | null = null;
const matchSubs = new Set<(m: MatchProgress | null) => void>();
const setMatch = (m: MatchProgress | null) => { match = m; matchSubs.forEach((f) => f(match)); };

// ---- a guest's tokens ----
function readTokens(): string[] {
  try { const t = JSON.parse(localStorage.getItem(GUEST_KEY) || "[]"); return Array.isArray(t) ? t : []; } catch { return []; }
}
function writeTokens(t: string[]) {
  try { if (t.length) localStorage.setItem(GUEST_KEY, JSON.stringify(t)); else localStorage.removeItem(GUEST_KEY); } catch { /* no storage */ }
}
// A token's round (the client may read them; only the server can make one).
function decode(token: string): { at: number; day: string; round: any } | null {
  try {
    const body = token.slice(0, token.indexOf("."));
    const d = JSON.parse(decodeURIComponent(escape(atob(body.replace(/-/g, "+").replace(/_/g, "/")))));
    return d && d.r && d.d ? { at: d.t | 0, day: String(d.d), round: d.r } : null;
  } catch { return null; }
}
function guestRaw(tokens: string[]): any {
  let p = prog.emptyProgress();
  const rounds = tokens.map(decode).filter(Boolean) as { at: number; day: string; round: any }[];
  for (const g of rounds.sort((a, b) => a.at - b.at)) p = prog.applyRound(p, g.round, g.day).progress;
  return p;
}
function showGuest() {
  if (demoState) return;
  const tokens = readTokens();
  const raw = guestRaw(tokens);
  set(view(raw.xp, raw, true, tokens.length));
}

// ---- the account ----
async function token(): Promise<string | null> {
  if (!supabase) return null;
  try { return (await supabase.auth.getSession()).data.session?.access_token ?? null; } catch { return null; }
}

async function loadAccount(userId: string) {
  if (!supabase) return;
  try {
    const { data } = await supabase.from("player_progress").select("xp, daily").eq("user_id", userId).maybeSingle();
    const raw = data ? { xp: data.xp | 0, ...(data.daily || {}) } : prog.emptyProgress();
    if (demoState) return;
    account = { userId, raw };
    set(view(raw.xp, raw, false, 0));
  } catch {
    account = { userId, raw: prog.emptyProgress() };
    set(view(0, null, false, 0));
  }
}

// A guest who signed in: their rounds count for the account now.
async function claimGuest(): Promise<void> {
  const tokens = readTokens();
  if (!tokens.length) return;
  const t = await token();
  if (!t) return;
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/progress/claim-guest`, {
      method: "POST", headers: { authorization: `Bearer ${t}`, "content-type": "application/json" }, body: JSON.stringify({ tokens }),
    });
    if (res.ok) { writeTokens([]); refreshEggBalance(); }
  } catch { /* kept for the next sign-in */ }
}

async function refresh() {
  if (!supabase) { showGuest(); return; }
  let userId: string | null = null;
  try { userId = (await supabase.auth.getSession()).data.session?.user?.id ?? null; } catch { userId = null; }
  if (!userId) { account = null; showGuest(); return; }
  await claimGuest();
  await loadAccount(userId);
}

// The design labs (?demo=1): a made-up player, level 4 and today's three part
// done, kept in memory only.
let demoState = false;
export function demoProgress(): void {
  demoState = true;
  const xp = 100 + 140 + 180 + 181;
  const lv = prog.levelOf(xp);
  const have: Record<string, number> = { "powerups-5": 5, "top3-3": 1, "gaps-12": 9 };
  const challenges = ["powerups-5", "top3-3", "gaps-12"].map((id) => {
    const c = prog.challengeById(id);
    return { id, tier: c.tier, metric: c.metric, target: c.target, text: c.text, detail: c.detail || null, have: have[id], done: have[id] >= c.target, xp: prog.CHALLENGE_XP[c.tier] };
  });
  set({ loaded: true, guest: false, xp, level: lv.level, into: lv.into, need: lv.need, day: today(), challenges, guestRounds: 0, toClaim: prog.DAILY_EGGS });
}

let started = false;
function start() {
  if (started || demoState) return;
  started = true;
  void refresh();
  if (supabase) supabase.auth.onAuthStateChange((event) => { if (event === "SIGNED_IN" || event === "SIGNED_OUT") void refresh(); });
}

export function useProgress(): Progress {
  const [p, setP] = useState(state);
  useEffect(() => {
    start();
    subs.add(setP);
    setP(state);
    return () => { subs.delete(setP); };
  }, []);
  return p;
}

// A new match: the tally starts over (a game is joined, a Play Now match or a
// Royale run is over and its card gone).
let demo = false;
export function resetMatchProgress(): void {
  if (match && !demo) setMatch(null);
}

// The design labs (debug pages): a match that paid, kept through resets.
export function demoMatchProgress(m?: MatchProgress): void {
  demo = true;
  setMatch(m ?? {
    xp: 184, fromLevel: 3, fromInto: 150, fromNeed: 180, level: 4, into: 154, need: 220,
    parts: [{ id: "played", n: 7, xp: 35 }, { id: "outlasted", n: 22, xp: 20 }, { id: "won", n: 1, xp: 10 }, { id: "gaps", n: 5, xp: 10 }, { id: "crashes", n: 3, xp: 9 }],
    quests: [{ text: "Grab 5 power-ups", xp: 100 }], levelEggs: 0, dayEggs: 0,
  });
}

export function useMatchProgress(): MatchProgress | null {
  const [m, setM] = useState(match);
  useEffect(() => {
    matchSubs.add(setM);
    setM(match);
    return () => { matchSubs.delete(setM); };
  }, []);
  return m;
}

// A round's XP parts added to the match's, by kind (a quest's XP is listed as
// the quest). Rounds played and won count rounds; the others their own count.
const PART_ORDER = ["played", "outlasted", "won", "gaps", "crashes"];
function addParts(have: XpPart[], round: { id: string; n?: number; xp: number }[] | undefined): XpPart[] {
  const out = have.map((p) => ({ ...p }));
  for (const p of round || []) {
    if (!p || PART_ORDER.indexOf(p.id) === -1) continue;
    let a = out.find((x) => x.id === p.id);
    if (!a) { a = { id: p.id, n: 0, xp: 0 }; out.push(a); }
    a.n += p.n != null ? p.n : 1;
    a.xp += p.xp | 0;
  }
  return out.sort((x, y) => PART_ORDER.indexOf(x.id) - PART_ORDER.indexOf(y.id));
}

function announce(before: { level: number; into: number; need?: number }, after: Progress, xp: number, parts: { id: string; n?: number; xp: number }[] | undefined, completed: { text: string; xp: number }[], levelEggs: number, dayEggs: number) {
  if (xp <= 0 && !completed.length && levelEggs + dayEggs <= 0) return;
  const need = before.need ?? prog.xpToNext(before.level);
  const m = match ?? { xp: 0, fromLevel: before.level, fromInto: before.into, fromNeed: need, level: before.level, into: before.into, need, parts: [], quests: [], levelEggs: 0, dayEggs: 0 };
  setMatch({
    ...m, xp: m.xp + xp, level: after.level, into: after.into, need: after.need,
    parts: addParts(m.parts, parts), quests: [...m.quests, ...completed],
    levelEggs: m.levelEggs + levelEggs, dayEggs: m.dayEggs + dayEggs,
  });
}

// The room's word on a signed-in player's round (progress_update).
export function takeProgressUpdate(m: any): void {
  if (!m || typeof m.total !== "number") return;
  const was = prog.levelOf(Math.max(0, m.total - (m.xp | 0)));
  const raw = { xp: m.total, day: m.day, counts: {}, done: (m.challenges || []).filter((c: Challenge) => c.done).map((c: Challenge) => c.id), allDone: !!m.allDone };
  if (account) account.raw = raw;
  const toClaim = typeof m.toClaim === "number" ? m.toClaim : state.toClaim;
  const after: Progress = { ...view(m.total, null, false, 0), challenges: m.day === today() ? m.challenges : prog.challengeStatus(null, today()), toClaim };
  set(after);
  const completed = (m.completed || []).map((id: string) => prog.challengeById(id)).filter(Boolean).map((c: any) => ({ text: c.text, xp: prog.CHALLENGE_XP[c.tier] }));
  const levelEggs = prog.levelEggsBetween(m.levelBefore, m.level).reduce((s: number, l: any) => s + l.eggs, 0);
  // (the day's eggs are not paid here: they wait on the start page for a click)
  announce(was, after, m.xp | 0, m.parts, completed, levelEggs, 0);
  if (m.eggs > 0) refreshEggBalance();
}

// The Golden Eggs for days with all three quests done, paid (edge-server
// progression/store.js, claim-daily). Resolves to what it paid and the balance
// after; `commit` takes them off the start page once the caller's eggs have
// flown in.
export async function claimDaily(): Promise<{ eggs: number; balance: number | null; commit: () => void } | { error: string }> {
  const commit = () => set({ ...state, toClaim: 0 });
  if (demoState) return { eggs: state.toClaim, balance: null, commit };
  const t = await token();
  if (!t) return { error: "not_signed_in" };
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/progress/claim-daily`, { method: "POST", headers: { authorization: `Bearer ${t}` } });
    const r = await res.json();
    if (!res.ok || typeof r.eggs !== "number") return { error: (r && r.error) || "claim_failed" };
    if (account) account.raw = { ...account.raw, unclaimed: [] };
    return { eggs: r.eggs, balance: typeof r.balance === "number" ? r.balance : null, commit };
  } catch {
    return { error: "network" };
  }
}

// A guest's round (progress_guest): kept, and counted here.
export function takeGuestRound(token: string): void {
  if (typeof token !== "string" || !decode(token)) return;
  const tokens = readTokens();
  if (tokens.length >= MAX_TOKENS) return;
  const before = state;
  const raw0 = guestRaw(tokens);
  const g = decode(token)!;
  const r = prog.applyRound(raw0, g.round, g.day);
  tokens.push(token);
  writeTokens(tokens);
  const after = view(r.progress.xp, r.progress, true, tokens.length);
  set(after);
  // (a guest's eggs wait for an account: none are announced)
  announce(before, after, r.xp, r.parts, r.completed.map((c: any) => ({ text: c.text, xp: prog.CHALLENGE_XP[c.tier] })), 0, 0);
}
